'use client'

/**
 * Purpose: the browser-side wrapper for PostHog application metrics — the only
 * module that touches `posthog.metrics` in client code, plus the `metrics`
 * config `instrumentation-client.ts` passes to `posthog.init`.
 *
 * Inputs: metric names, values, and low-cardinality attributes from call sites;
 * build-time `NEXT_PUBLIC_VERCEL_ENV` / `NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA` /
 * `NODE_ENV` for resource attributes; `window.location.origin` for URL templating.
 *
 * Outputs: samples aggregated by posthog-js and sent via `/ingest/i/v1/metrics`.
 * The SDK discards samples while capture is off (before analytics consent). Automatic
 * `http.client.request.duration` for every fetch / XHR, keyed by a route template.
 */
import posthog, { type BrowserMetricsConfig } from 'posthog-js'
import { scrubMetricAttributes, toUrlTemplate } from '@lib/metrics-url-template'

type MetricAttributes = Record<string, string | number | boolean>

/** Options for a single sample. `unit` is part of series identity — always set it on histograms. */
export type MetricOptions = { unit?: string; attributes?: MetricAttributes }

/** Distinct from the server's `lamaven`, so browser and server series can be filtered apart. */
export const BROWSER_METRICS_SERVICE_NAME = 'lamaven-web'

/** posthog-js's default name for automatic network metrics, set explicitly so the docs can reference it. */
export const NETWORK_METRIC = 'http.client.request.duration'

type BrowserMetricsEnv = {
  NEXT_PUBLIC_VERCEL_ENV?: string
  NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA?: string
  NODE_ENV?: string
}

/**
 * `NEXT_PUBLIC_*` values are only inlined into the client bundle as static
 * member accesses, so they are read one by one rather than passing `process.env`.
 */
function resolveEnv(): BrowserMetricsEnv {
  return {
    NEXT_PUBLIC_VERCEL_ENV: process.env.NEXT_PUBLIC_VERCEL_ENV,
    NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
    NODE_ENV: process.env.NODE_ENV,
  }
}

/** The page's URL template for a request, or null when it must not be recorded (PostHog's own proxy). */
function templateFor(url: string): string | null {
  return toUrlTemplate(url, window.location.origin)
}

/**
 * `metrics` options for `posthog.init`: service identity, the PII /
 * identifier scrubber, and automatic network timing with project URL templates.
 */
export function browserMetricsConfig(processEnv: BrowserMetricsEnv = resolveEnv()): BrowserMetricsConfig {
  const sha = processEnv.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA
  return {
    serviceName: BROWSER_METRICS_SERVICE_NAME,
    environment: processEnv.NEXT_PUBLIC_VERCEL_ENV || processEnv.NODE_ENV || 'development',
    // Short SHA keeps the version readable in the Metrics UI; absent off Vercel.
    ...(sha ? { serviceVersion: sha.slice(0, 7) } : {}),
    beforeSend: scrubMetricAttributes,
    network: {
      // A falsy name skips the request — used for the /ingest proxy.
      name: (request) => (templateFor(request.url) === null ? null : NETWORK_METRIC),
      // Overrides the SDK's digits/UUID-only template with locale, slug, cuid and third-party rules.
      attributes: (request) => {
        const template = templateFor(request.url)
        return template === null ? undefined : { 'url.template': template }
      },
    },
  }
}

/** Adds to a counter — things that only go up. Value defaults to 1. */
export function countMetric(name: string, value = 1, options?: MetricOptions): void {
  posthog.metrics.count(name, value, options)
}

/** Records the current value of something that goes up and down. */
export function gaugeMetric(name: string, value: number, options?: MetricOptions): void {
  posthog.metrics.gauge(name, value, options)
}

/** Records one observation of a distribution (latency, payload size). */
export function histogramMetric(name: string, value: number, options?: MetricOptions): void {
  posthog.metrics.histogram(name, value, options)
}
